import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../hooks/useProfile';
import { useGmbStatus } from '../hooks/useGmbStatus';
import { useSubscription } from '../hooks/useSubscription';
import { useCsvImports } from '../hooks/useCsvImports';
import { logoutAndRedirect } from '../utils/logout';
import { openExternalUrl, urlToWeb } from '../utils/external';
import ProfileCard from '../components/ProfileCard';
import RestaurantCard from '../components/RestaurantCard';
import SettingsSection from '../components/SettingsSection';
import IntegrationRow from '../components/IntegrationRow';
import QrPreviewCard from '../components/QrPreviewCard';
import QrFullscreenModal from '../components/QrFullscreenModal';
import SubscriptionRow from '../components/SubscriptionRow';
import CsvImportRow from '../components/CsvImportRow';
import MenuRow from '../components/MenuRow';
import EmptyState from '../components/EmptyState';
import EditProfileSheet from '../sections/EditProfileSheet';
import EditRestaurantSheet from '../sections/EditRestaurantSheet';
import SwitchRestaurantSheet from '../sections/SwitchRestaurantSheet';
import OAuthGmbSheet from '../sections/OAuthGmbSheet';

const APP_VERSION = 'Revyo v1.0.0';

export default function MobileProfil() {
  const navigate = useNavigate();
  const { user, restaurant, restaurants, loading, error, updateProfile, updateRestaurant } = useProfile();
  const gmb = useGmbStatus();
  const sub = useSubscription();
  const csv = useCsvImports();

  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editRestoOpen, setEditRestoOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [oauthOpen, setOauthOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleLogout = async () => {
    if (!window.confirm('Confirmer la déconnexion ?')) return;
    setLogoutLoading(true);
    await logoutAndRedirect(navigate);
  };

  const handleStartOAuth = async () => {
    await gmb.startOAuth();
    // window.location.href déclenché par le hook → l'app sort vers Google
  };

  const handleDisconnectGmb = async () => {
    if (!window.confirm('Déconnecter Google Business ?')) return;
    try { await gmb.disconnect(); } catch (e) { console.warn(e); }
  };

  if (loading && !user && !restaurant) {
    return (
      <div style={styles.page}>
        <div style={styles.skeletonHero} />
        {[1, 2, 3].map(i => <div key={i} style={styles.skeletonRow} />)}
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Profil</h1>
        <p style={styles.subtitle}>Gérer mon compte et mon restaurant</p>
      </div>

      <div style={styles.content}>
        {error && !user && (
          <EmptyState
            emoji="⚠️"
            title="Impossible de charger votre profil"
            subtitle={String(error)}
          />
        )}

        {user && <ProfileCard user={user} onEdit={() => setEditProfileOpen(true)} />}

        {restaurant && (
          <RestaurantCard
            restaurant={restaurant}
            count={restaurants.length || 1}
            onConfigure={() => setEditRestoOpen(true)}
            onSwitch={() => setSwitchOpen(true)}
          />
        )}

        <SettingsSection title="🔌 Intégrations">
          <IntegrationRow
            icon="🌟"
            name="Google Business Profile"
            description={
              gmb.connected
                ? 'Vos réponses aux avis sont publiées sur votre fiche Google.'
                : "Connectez-vous pour publier vos réponses sur Google."
            }
            connected={gmb.connected}
            loading={gmb.loading}
            onConnect={() => setOauthOpen(true)}
            onDisconnect={handleDisconnectGmb}
          />
        </SettingsSection>

        {restaurant && (
          <QrPreviewCard restaurant={restaurant} onOpenFullscreen={() => setQrOpen(true)} />
        )}

        <SettingsSection title="💳 Abonnement">
          <div style={{ padding: 16 }}>
            <SubscriptionRow
              plan={sub.plan}
              planStatus={sub.planStatus}
              cancelAtPeriodEnd={sub.cancelAtPeriodEnd}
              currentPeriodEnd={sub.currentPeriodEnd}
              onManage={sub.openPortal}
              onSubscribe={sub.openPricing}
            />
          </div>
        </SettingsSection>

        <SettingsSection
          title="📥 Import CSV"
          footer="Pour importer un nouveau fichier, utilisez la version web."
        >
          {csv.loading ? (
            <div style={styles.csvLoading}>Chargement…</div>
          ) : csv.imports.length === 0 ? (
            <div style={styles.csvEmpty}>Aucun import pour le moment.</div>
          ) : (
            csv.imports.slice(0, 3).map((imp) => (
              <CsvImportRow key={imp.id} importItem={imp} />
            ))
          )}
          <MenuRow
            icon="↗"
            label="Importer un fichier (web)"
            onClick={csv.openWebUpload}
          />
        </SettingsSection>

        <SettingsSection title="Analyse">
          <MenuRow
            icon="📍"
            label="Concurrents"
            onClick={() => navigate('/m/concurrents')}
          />
        </SettingsSection>

        <SettingsSection title="Personnalisation">
          <MenuRow
            icon="🎨"
            label="Page client (messages, couleurs, logo)"
            onClick={() => navigate('/m/page-client')}
          />
        </SettingsSection>

        <SettingsSection title="Compte">
          <MenuRow
            icon="👤"
            label="Mon compte (e-mail, mot de passe)"
            onClick={() => navigate('/m/compte')}
          />
          <MenuRow
            icon="🌐"
            label="Ouvrir le tableau de bord web"
            onClick={() => openExternalUrl(urlToWeb('/dashboard'))}
          />
          <MenuRow
            icon="↪"
            label="Se déconnecter"
            variant="danger"
            disabled={logoutLoading}
            onClick={handleLogout}
          />
        </SettingsSection>

        <p style={styles.version}>{APP_VERSION}</p>
      </div>

      <EditProfileSheet
        open={editProfileOpen}
        user={user}
        onClose={() => setEditProfileOpen(false)}
        onSave={updateProfile}
      />

      <EditRestaurantSheet
        open={editRestoOpen}
        restaurant={restaurant}
        onClose={() => setEditRestoOpen(false)}
        onSave={updateRestaurant}
      />

      <SwitchRestaurantSheet
        open={switchOpen}
        onClose={() => setSwitchOpen(false)}
        restaurants={restaurants}
        activeId={restaurant?.id || restaurants?.[0]?.id}
      />

      <OAuthGmbSheet
        open={oauthOpen}
        onClose={() => setOauthOpen(false)}
        onContinue={handleStartOAuth}
      />

      <QrFullscreenModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        restaurant={restaurant}
      />

      <style>{`
        @keyframes revyoPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100dvh',
    background: '#EDEAE3',
    paddingBottom: 32,
  },
  header: {
    paddingTop: 'env(safe-area-inset-top)',
    padding: '16px 16px 4px',
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    color: '#1A1A1A',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 13,
    color: '#6B6B6B',
  },
  content: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  csvLoading: {
    padding: 16,
    fontSize: 13,
    color: '#6B6B6B',
    textAlign: 'center',
  },
  csvEmpty: {
    padding: 16,
    fontSize: 13,
    color: '#9A9A9A',
    textAlign: 'center',
  },
  version: {
    margin: 0,
    textAlign: 'center',
    color: '#9A9A9A',
    fontSize: 11,
    paddingTop: 8,
  },
  skeletonHero: {
    margin: 16,
    height: 100,
    background: '#E5E5E0',
    borderRadius: 16,
    animation: 'revyoPulse 1.4s ease-in-out infinite',
  },
  skeletonRow: {
    margin: '0 16px 16px',
    height: 80,
    background: '#E5E5E0',
    borderRadius: 16,
    animation: 'revyoPulse 1.4s ease-in-out infinite',
  },
};
